import * as XLSX from "xlsx";
import { getLifeBaseImportMaxRowsMessage, LIFE_BASE_IMPORT_MAX_ROWS } from "./lifeBaseCatalog";

const DATE_FIELD_ALIASES = new Set([
  "data de nascimento",
  "data_nascimento",
  "datanascimento",
]);

function normalizeHeader(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function isBirthDateHeader(header: string) {
  return DATE_FIELD_ALIASES.has(normalizeHeader(header));
}

function formatExcelDate(serial: number) {
  const parsed = XLSX.SSF.parse_date_code(serial);
  if (!parsed || !parsed.y || !parsed.m || !parsed.d) return null;
  const day = String(parsed.d).padStart(2, "0");
  const month = String(parsed.m).padStart(2, "0");
  const year = String(parsed.y).padStart(4, "0");
  return `${day}/${month}/${year}`;
}

function normalizeCellValue(header: string, cell: XLSX.CellObject | undefined) {
  if (!cell) return "";

  if (isBirthDateHeader(header)) {
    if (typeof cell.v === "number") {
      return formatExcelDate(cell.v) ?? (cell.w ?? cell.v);
    }

    if (cell.v instanceof Date && !Number.isNaN(cell.v.getTime())) {
      const day = String(cell.v.getDate()).padStart(2, "0");
      const month = String(cell.v.getMonth() + 1).padStart(2, "0");
      const year = String(cell.v.getFullYear()).padStart(4, "0");
      return `${day}/${month}/${year}`;
    }
  }

  return cell.w ?? cell.v ?? "";
}

/**
 * Lê a planilha de Carga de Vidas e retorna as linhas de dados indexadas pelo cabeçalho original.
 * Usado tanto pelo upload do Portal (ArrayBuffer do navegador) quanto pelo recebimento de arquivo
 * via API (Buffer do Node, que é um Uint8Array) — mesma leitura para as duas origens.
 */
export function parseLifeBaseWorkbookRows(
  buffer: ArrayBuffer | Uint8Array,
  preferredSheetName?: string | null,
  maxRows: number = LIFE_BASE_IMPORT_MAX_ROWS,
) {
  const workbook = XLSX.read(buffer, { type: "array", cellDates: true });
  const selectedSheetName = preferredSheetName && workbook.SheetNames.includes(preferredSheetName)
    ? preferredSheetName
    : workbook.SheetNames[0];

  if (!selectedSheetName) {
    throw new Error("A planilha está vazia ou não contém abas legíveis.");
  }

  const worksheet = workbook.Sheets[selectedSheetName];
  const range = XLSX.utils.decode_range(worksheet["!ref"] ?? "A1:A1");
  const headers: string[] = [];

  for (let column = range.s.c; column <= range.e.c; column += 1) {
    const address = XLSX.utils.encode_cell({ r: range.s.r, c: column });
    const headerCell = worksheet[address];
    headers.push(String(headerCell?.w ?? headerCell?.v ?? "").trim());
  }

  if (headers.every((header) => header === "")) {
    throw new Error("Os cabeçalhos da planilha não foram encontrados.");
  }

  const rows: Array<Record<string, unknown>> = [];

  for (let row = range.s.r + 1; row <= range.e.r; row += 1) {
    const record: Record<string, unknown> = {};
    let hasAnyValue = false;

    for (let column = range.s.c; column <= range.e.c; column += 1) {
      const header = headers[column - range.s.c] ?? "";
      if (!header) continue;
      const address = XLSX.utils.encode_cell({ r: row, c: column });
      const cell = worksheet[address];
      const value = normalizeCellValue(header, cell);
      if (String(value).trim() !== "") hasAnyValue = true;
      record[header] = value;
    }

    if (hasAnyValue) {
      rows.push(record);
      if (rows.length > maxRows) {
        throw new Error(getLifeBaseImportMaxRowsMessage(rows.length));
      }
    }
  }

  if (rows.length === 0) {
    throw new Error("Nenhuma linha de dados foi encontrada na planilha.");
  }

  return rows;
}
